import React, { Component } from 'react';
import holdsMessages from '../utilities/holdsMessages';

function withValidator(Form, config) {
  return class extends Component {
    constructor(props) {
      super(props);
      this.classes = holdsMessages();
      this.errors = holdsMessages();
      this.touched = {};
      this.state = {
        cssValidationClasses: {},
        errorFeedback: {},
        hasErrors: false,
        hasUntouched: true,
        touched: {},
      };
    }

    // ----------------------
    // Computed
    // ----------------------

    get cssValidationClasses() {
      return this.classes.getMessages();
    }

    get errorFeedback() {
      return this.errors.getMessages();
    }

    get hasErrors() {
      return !!Object.keys(this.errors).length;
    }

    get isInvalid() {
      return !!Object.keys(config.fields).find(field => !this.isValid(field));
    }

    get hasUntouched() {
      return !!Object.keys(config.fields).find((field) => {
        return !Object.keys(this.touched).includes(field);
      });
    }

    // ----------------------
    // Methods
    // ----------------------

    addValidCssClass(key, isFocused) {
      return isFocused || this.isTouched(key)
        ? config.cssClasses.fieldValid
        : this.classes.getMessage(key);
    }

    addInvalidCssClass(key) {
      return this.isTouched(key)
        ? config.cssClasses.fieldInvalid
        : this.classes.getMessage(key);
    }

    fieldHasError(key, value) {
      return config.fields[key].rules.find((rules) => {
        return rules.test(value) ? false : rules.feedback;
      });
    }

    isTouched(field) {
      return Object.keys(this.touched).includes(field);
    }

    isValid(field) {
      return this.classes.getMessage(field) === config.cssClasses.fieldValid;
    }

    initialize(fieldsMap) {
      Object.keys(fieldsMap).forEach((field) => {
        this.touch(field);
        this.validateField(field, fieldsMap[field], true);
      });
    }

    shouldClearCssClass(key, className, isFocused) {
      return isFocused && this.classes.getMessage(key) === className;
    }

    shouldClearErrors(key, isFocused) {
      this.classes.update(key, this.addValidCssClass(key, isFocused));
      return isFocused ? true : !!this.errors.getMessage(key);
    }

    shouldUpdateErrors(key, error, isFocused) {
      // By default--and regardless of whether we set an invalid CSS class--make sure
      //   we're not showing a valid CSS class if we have an error
      if (this.shouldClearCssClass(key, config.cssClasses.fieldValid, isFocused)) {
        this.classes.clear(key);
      }
      this.classes.update(key, this.addInvalidCssClass(key));
      const bool =
        isFocused && !this.isTouched(key)
          ? false
          : this.errors.getMessage(key) !== error.feedback;
      return bool;
    }

    touch(field) {
      this.touched = { ...this.touched, [field]: true };
      this.setState(() => ({ touched: this.touched }));
    }

    validateField(key, value, isFocused) {
      const error = this.fieldHasError(key, value);
      // eslint-disable-next-line no-nested-ternary
      const errorFeedback = error && this.shouldUpdateErrors(key, error, isFocused)
        ? this.errors.update(key, error.feedback) // Add error, return update messages
        : !error && this.shouldClearErrors(key, isFocused)
          ? this.errors.clear(key) // Clear error, return update messages
          : this.errors.getMessages(); // Do nothing, return current messages
      this.setState(() => ({
        cssValidationClasses: this.classes.getMessages(),
        errorFeedback,
        hasErrors: !!this.errors.getMessageCount(),
        hasUntouched: this.hasUntouched,
        touched: this.touched,
      }));
    }

    // ----------------------
    // Render
    // ----------------------

    render() {
      return (
        <Form
          {...this.props}
          cssValidationClasses={this.state.cssValidationClasses}
          errorFeedback={this.state.errorFeedback}
          hasErrors={this.state.hasErrors}
          hasUntouched={this.state.hasUntouched}
          initialize={fieldsMap => this.initialize(fieldsMap)}
          touch={field => this.touch(field)}
          touched={this.state.touched}
          validateField={(key, value, isFocused) => this.validateField(key, value, isFocused)}
        />
      );
    }
  };
}

export default withValidator;
